// /catalog/plans — admin lista global de External Plans por SaaS Product.
// Acessível pelo Catálogo (sub-nav junto com Produtos SaaS).
// DR-013: planos por SaaS são blocos de construção dos planos comerciais bundle (Story 2.5).

import * as Dialog from '@radix-ui/react-dialog';
import { AlertCircle, Layers, Pencil, Plus, RefreshCw, Search, Trash2, X } from 'lucide-react';
import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import type {
  CreateExternalPlanDto,
  CreateExternalPlanInput,
  QueryExternalPlansDto,
} from '@control-center/shared';
import { formatBR } from '@control-center/shared';

import { cn } from '@control-center/ui';

import { CatalogTabs } from '../../components/catalog/CatalogTabs';
import { PageHelp } from '../../components/common/PageHelp';
import { Field, Input, Select } from '../../components/clients/form-primitives';
import { PillButton } from '../../components/clients/PillButton';
import { ExternalPlanForm } from '../../components/external-plans/ExternalPlanForm';
import {
  useCreateExternalPlan,
  useDeleteExternalPlan,
  useExternalPlans,
  useUpdateExternalPlan,
} from '../../hooks/use-external-plans';
import { useSaasProducts } from '../../hooks/use-saas-products';
import { extractErrorMessage } from '../../lib/errors';
import type { ExternalPlanEntity } from '../../lib/external-plans-api';
import { useAuthStore } from '../../stores/useAuthStore';

const STATUS_LABEL: Record<ExternalPlanEntity['status'], string> = {
  ativo: 'Ativo',
  oculto: 'Oculto',
  legado: 'Legado',
  descontinuado: 'Descontinuado',
};

const STATUS_DOT: Record<ExternalPlanEntity['status'], string> = {
  ativo: 'bg-good ring-good/15',
  oculto: 'bg-muted-2 ring-muted-3/15',
  legado: 'bg-warn ring-warn/15',
  descontinuado: 'bg-bad ring-bad/15',
};

export function ExternalPlansPage(): JSX.Element {
  const role = useAuthStore((s) => s.user?.role);
  const isAdmin = role === 'admin';

  const [q, setQ] = useState('');
  const [saasFilter, setSaasFilter] = useState<'all' | string>('all');
  const [statusFilter, setStatusFilter] = useState<'all' | ExternalPlanEntity['status']>('all');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState<ExternalPlanEntity | null>(null);

  const filters: QueryExternalPlansDto = useMemo(() => {
    const out: QueryExternalPlansDto = { limit: 50 };
    if (q.trim()) out.q = q.trim();
    if (saasFilter !== 'all') out.saas_product_id = saasFilter;
    if (statusFilter !== 'all') out.status = statusFilter;
    return out;
  }, [q, saasFilter, statusFilter]);

  const saasList = useSaasProducts({ limit: 100 });
  const list = useExternalPlans(filters);
  const create = useCreateExternalPlan();
  const update = useUpdateExternalPlan();
  const remove = useDeleteExternalPlan();

  const saasProducts = useMemo(() => saasList.data?.items ?? [], [saasList.data]);

  // Map saasProductId → name pra display rápido na lista
  const saasNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const s of saasProducts) map.set(s.id, s.name);
    return map;
  }, [saasProducts]);

  function openNew(): void {
    setEditing(null);
    setFormOpen(true);
  }

  function openEdit(p: ExternalPlanEntity): void {
    setEditing(p);
    setFormOpen(true);
  }

  async function handleSubmit(values: CreateExternalPlanDto): Promise<void> {
    try {
      if (editing) {
        const { saas_product_id: _saas, slug, ...rest } = values;
        void _saas;
        const payload = slug === editing.slug ? rest : { ...rest, slug };
        await update.mutateAsync({ id: editing.id, payload });
        toast.success('Plano atualizado.');
      } else {
        await create.mutateAsync(values);
        toast.success('Plano criado.');
      }
      setFormOpen(false);
      setEditing(null);
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos salvar.'));
    }
  }

  async function handleDelete(p: ExternalPlanEntity): Promise<void> {
    const ok = window.confirm(
      `Descontinuar "${p.name}"? Status muda pra "descontinuado" e mantém histórico.`,
    );
    if (!ok) return;
    try {
      await remove.mutateAsync(p.id);
      toast.success('Plano descontinuado.');
    } catch (err) {
      toast.error(extractErrorMessage(err, 'Não conseguimos descontinuar o plano.'));
    }
  }

  return (
    <div className="space-y-5">
      <CatalogTabs />

      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex items-start gap-2">
          <div>
            <h1 className="m-0 text-[26px] font-bold tracking-[-0.025em] text-ink">
              Planos externos
            </h1>
            <p className="mt-1 text-[13px] text-muted">
              Planos individuais por SaaS — blocos de construção dos planos comerciais bundle.
            </p>
          </div>
          <PageHelp id="external-plans" />
        </div>
        <div className="flex items-center gap-2">
          <PillButton
            icon={<RefreshCw size={14} strokeWidth={2} />}
            disabled
            type="button"
            title="Disponível quando o nível de integração do SaaS for read_only ou superior (Fase 4)."
          >
            Sincronizar do SaaS
          </PillButton>
          {isAdmin && (
            <PillButton solid icon={<Plus size={14} strokeWidth={2} />} onClick={openNew}>
              Novo plano
            </PillButton>
          )}
        </div>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="min-w-[220px] flex-1">
          <Field label="Buscar">
            {({ inputId }) => (
              <div className="relative">
                <Search
                  size={14}
                  strokeWidth={2}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-2"
                />
                <Input
                  id={inputId}
                  type="text"
                  placeholder="Nome ou slug"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  className="pl-9"
                />
              </div>
            )}
          </Field>
        </div>
        <div className="min-w-[200px]">
          <Field label="SaaS Product">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={saasFilter}
                onChange={(e) => setSaasFilter(e.target.value)}
              >
                <option value="all">Todos</option>
                {saasProducts.map((s) => (
                  <option key={s.id} value={s.id}>
                    {s.name}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
        <div className="min-w-[160px]">
          <Field label="Status">
            {({ inputId }) => (
              <Select
                id={inputId}
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as 'all' | ExternalPlanEntity['status'])
                }
              >
                <option value="all">Todos</option>
                {(['ativo', 'oculto', 'legado', 'descontinuado'] as const).map((s) => (
                  <option key={s} value={s}>
                    {STATUS_LABEL[s]}
                  </option>
                ))}
              </Select>
            )}
          </Field>
        </div>
      </div>

      {/* Lista */}
      {list.isLoading ? (
        <SkeletonList />
      ) : list.isError ? (
        <ErrorBox
          message={extractErrorMessage(list.error, 'Não conseguimos carregar.')}
          onRetry={() => void list.refetch()}
        />
      ) : list.data && list.data.items.length > 0 ? (
        <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
          <table className="w-full text-[13px]">
            <thead className="border-b border-line bg-panel-2">
              <tr className="text-left text-[11px] uppercase tracking-wider text-muted-2">
                <th className="px-4 py-3 font-medium">Plano</th>
                <th className="px-4 py-3 font-medium">SaaS</th>
                <th className="px-4 py-3 font-medium">Status</th>
                <th className="px-4 py-3 font-medium">Sync</th>
                {isAdmin && <th className="px-4 py-3" />}
              </tr>
            </thead>
            <tbody>
              {list.data.items.map((p) => (
                <tr key={p.id} className="border-b border-line last:border-b-0 hover:bg-panel-2">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[8px] bg-panel-3 text-muted">
                        <Layers size={14} strokeWidth={1.8} />
                      </span>
                      <div className="min-w-0">
                        <div className="truncate font-medium text-ink">{p.name}</div>
                        <code className="block truncate font-mono text-[11px] text-muted-2">
                          {p.slug}
                        </code>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-muted">
                    {saasNameById.get(p.saasProductId) ?? (
                      <code className="font-mono text-[11px] text-muted-2">
                        {p.saasProductId.slice(0, 8)}…
                      </code>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={cn(
                        'inline-flex items-center gap-[6px] text-[11px] font-medium',
                        p.status === 'ativo' ? 'text-ink-2' : 'text-muted',
                      )}
                    >
                      <span
                        aria-hidden
                        className={cn(
                          'h-[7px] w-[7px] rounded-full ring-[3px]',
                          STATUS_DOT[p.status],
                        )}
                      />
                      {STATUS_LABEL[p.status]}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[12px] text-muted-2">
                    {p.lastSyncedAt ? formatBR(p.lastSyncedAt) : '—'}
                  </td>
                  {isAdmin && (
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          type="button"
                          aria-label={`Editar ${p.name}`}
                          onClick={() => openEdit(p)}
                          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-ink"
                        >
                          <Pencil size={13} strokeWidth={1.8} />
                        </button>
                        <button
                          type="button"
                          aria-label={`Descontinuar ${p.name}`}
                          onClick={() => void handleDelete(p)}
                          disabled={p.status === 'descontinuado'}
                          className="flex h-8 w-8 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-3 hover:text-bad disabled:cursor-not-allowed disabled:opacity-40"
                        >
                          <Trash2 size={13} strokeWidth={1.8} />
                        </button>
                      </div>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          isAdmin={isAdmin}
          hasFilters={Boolean(q || saasFilter !== 'all' || statusFilter !== 'all')}
          onAdd={openNew}
        />
      )}

      {/* Dialog: criar / editar */}
      {isAdmin && (
        <Dialog.Root open={formOpen} onOpenChange={setFormOpen}>
          <Dialog.Portal>
            <Dialog.Overlay
              className={cn(
                'fixed inset-0 z-40 bg-black/60 backdrop-blur-[2px]',
                'data-[state=open]:animate-in data-[state=closed]:animate-out',
                'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
              )}
            />
            <Dialog.Content
              className={cn(
                'fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2',
                'w-[min(600px,calc(100vw-32px))] max-h-[calc(100vh-48px)] overflow-y-auto',
                'rounded-[14px] border border-line bg-panel shadow-lg p-7',
                'focus:outline-none',
              )}
            >
              <div className="mb-5 flex items-start justify-between gap-4">
                <Dialog.Title className="text-[20px] font-semibold tracking-[-0.01em] text-ink">
                  {editing ? `Editar ${editing.name}` : 'Novo plano'}
                </Dialog.Title>
                <Dialog.Close asChild>
                  <button
                    type="button"
                    aria-label="Fechar"
                    className="flex h-8 w-8 shrink-0 items-center justify-center rounded-[6px] text-muted transition-colors hover:bg-panel-2 hover:text-ink"
                  >
                    <X size={15} strokeWidth={2} />
                  </button>
                </Dialog.Close>
              </div>

              <ExternalPlanForm
                editMode={Boolean(editing)}
                saasProducts={saasProducts}
                defaultValues={editing ? toFormDefaults(editing) : undefined}
                submitLabel={editing ? 'Salvar alterações' : 'Criar plano'}
                isPending={create.isPending || update.isPending}
                onSubmit={handleSubmit}
                onCancel={() => setFormOpen(false)}
              />
            </Dialog.Content>
          </Dialog.Portal>
        </Dialog.Root>
      )}
    </div>
  );
}

// =============================================================================
// Sub-components
// =============================================================================

function SkeletonList(): JSX.Element {
  return (
    <div className="overflow-hidden rounded-[14px] border border-line bg-panel">
      {[0, 1, 2, 3, 4].map((i) => (
        <div
          key={i}
          aria-hidden
          className="h-[60px] animate-pulse border-b border-line bg-panel-2 last:border-b-0"
        />
      ))}
    </div>
  );
}

function ErrorBox({ message, onRetry }: { message: string; onRetry: () => void }): JSX.Element {
  return (
    <div className="rounded-[14px] border border-bad/30 bg-bad/5 p-5 text-[13px]">
      <div className="flex items-center gap-2 font-medium text-bad">
        <AlertCircle size={14} strokeWidth={2} aria-hidden />
        Não conseguimos carregar os planos.
      </div>
      <p className="mt-1 text-muted">{message}</p>
      <div className="mt-3">
        <PillButton onClick={onRetry}>Tentar de novo</PillButton>
      </div>
    </div>
  );
}

function EmptyState({
  isAdmin,
  hasFilters,
  onAdd,
}: {
  isAdmin: boolean;
  hasFilters: boolean;
  onAdd: () => void;
}): JSX.Element {
  if (hasFilters) {
    return (
      <div className="rounded-[14px] border border-line bg-panel-2 p-6 text-center">
        <p className="text-[13px] text-muted">Nenhum plano bate com os filtros.</p>
      </div>
    );
  }
  return (
    <div className="rounded-[14px] border border-line bg-panel-2 p-8 text-center">
      <div className="mx-auto mb-3 flex h-10 w-10 items-center justify-center rounded-full bg-panel-3 text-muted-2">
        <Layers size={18} strokeWidth={1.5} />
      </div>
      <p className="text-[14px] font-medium text-ink-2">Sem planos cadastrados ainda.</p>
      <p className="mt-1 text-[12px] text-muted">
        Cadastre os planos individuais de cada SaaS (Growth, Pro, Enterprise…).
      </p>
      {isAdmin && (
        <div className="mt-4 flex justify-center">
          <PillButton solid icon={<Plus size={14} strokeWidth={2} />} onClick={onAdd}>
            Cadastrar primeiro plano
          </PillButton>
        </div>
      )}
    </div>
  );
}

function toFormDefaults(p: ExternalPlanEntity): Partial<CreateExternalPlanInput> {
  return {
    saas_product_id: p.saasProductId,
    slug: p.slug,
    name: p.name,
    description: p.description ?? '',
    status: p.status,
    metadata: (p.metadata as Record<string, unknown> | null) ?? {},
  };
}
